import React, { useState, useEffect, useMemo } from 'react';
import { Link, Copy, Download, Shuffle, Eye, Settings, Zap, Code, List, Globe, Hash } from 'lucide-react';

interface SlugMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  example: string;
  separator: string;
}

interface SlugSettings {
  removeAccents: boolean;
  preserveCase: boolean;
  strictMode: boolean;
  maxLength: number;
  minLength: number;
  removeNumbers: boolean;
  preserveNumbers: boolean;
  removeSpecialChars: boolean;
  customSeparator: string;
  addPrefix: string;
  addSuffix: string;
  removeStopWords: boolean;
  customStopWords: string;
  trimWhitespace: boolean;
  removeEmpty: boolean;
  removeDuplicates: boolean;
  sortItems: boolean;
  reverseSort: boolean;
  limitCount: boolean;
  maxCount: number;
  addTimestamp: boolean;
  addRandomSuffix: boolean;
  camelCaseInput: boolean;
  preserveExtensions: boolean;
  seoFriendly: boolean;
  unicodeSupport: boolean;
}

interface SlugResult {
  inputItems: string[];
  finalItems: string[];
  method: string;
  output: string;
  stats: SlugStatistics;
}

interface SlugStatistics {
  originalCount: number;
  finalCount: number;
  emptyRemoved: number;
  duplicatesRemoved: number;
  totalInputLength: number;
  totalOutputLength: number;
  avgInputLength: number;
  avgOutputLength: number;
  longestInput: string;
  shortestInput: string;
  longestOutput: string;
  shortestOutput: string;
  compressionRatio: number;
  urlSafeCount: number;
  seoScore: number;
}

const SlugGenerator: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('kebab');
  const [outputText, setOutputText] = useState('');
  const [copyMessage, setCopyMessage] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);

  const [settings, setSettings] = useState<SlugSettings>({
    removeAccents: true,
    preserveCase: false,
    strictMode: false,
    maxLength: 100,
    minLength: 1,
    removeNumbers: false,
    preserveNumbers: true,
    removeSpecialChars: true,
    customSeparator: '-',
    addPrefix: '',
    addSuffix: '',
    removeStopWords: false,
    customStopWords: '',
    trimWhitespace: true,
    removeEmpty: true,
    removeDuplicates: false,
    sortItems: false,
    reverseSort: false,
    limitCount: false,
    maxCount: 100,
    addTimestamp: false,
    addRandomSuffix: false,
    camelCaseInput: false,
    preserveExtensions: false,
    seoFriendly: true,
    unicodeSupport: false,
  });

  const slugMethods: SlugMethod[] = [
    {
      id: 'kebab',
      name: 'Kebab Case Slug',
      description: 'Standard URL-friendly lowercase with hyphens',
      icon: <Link className="w-4 h-4" />,
      color: 'blue',
      example: 'my-awesome-blog-post',
      separator: '-'
    },
    {
      id: 'underscore',
      name: 'Underscore Slug',
      description: 'Lowercase with underscores',
      icon: <Code className="w-4 h-4" />,
      color: 'green',
      example: 'my_awesome_blog_post',
      separator: '_'
    },
    {
      id: 'dot',
      name: 'Dot Separated',
      description: 'Lowercase with dots',
      icon: <Globe className="w-4 h-4" />,
      color: 'purple',
      example: 'my.awesome.blog.post',
      separator: '.'
    },
    {
      id: 'camelCase',
      name: 'camelCase Slug',
      description: 'camelCase format for APIs',
      icon: <Zap className="w-4 h-4" />,
      color: 'red',
      example: 'myAwesomeBlogPost',
      separator: ''
    },
    {
      id: 'PascalCase',
      name: 'PascalCase Slug',
      description: 'PascalCase format',
      icon: <Hash className="w-4 h-4" />,
      color: 'yellow',
      example: 'MyAwesomeBlogPost',
      separator: ''
    },
    {
      id: 'numbers',
      name: 'Numeric Slug',
      description: 'Separated with numbers',
      icon: <Hash className="w-4 h-4" />,
      color: 'indigo',
      example: 'my1awesome1blog1post',
      separator: '1'
    },
    {
      id: 'compact',
      name: 'Compact Slug',
      description: 'Remove all spaces, keep letters/numbers',
      icon: <Zap className="w-4 h-4" />,
      color: 'pink',
      example: 'myawesomeblogpost',
      separator: ''
    },
    {
      id: 'seo',
      name: 'SEO Optimized',
      description: 'SEO-friendly with stop word removal',
      icon: <Globe className="w-4 h-4" />,
      color: 'orange',
      example: 'awesome-blog-post',
      separator: '-'
    },
    {
      id: 'filename',
      name: 'Filename Safe',
      description: 'Safe for file names and paths',
      icon: <Code className="w-4 h-4" />,
      color: 'teal',
      example: 'my_awesome_blog_post',
      separator: '_'
    },
    {
      id: 'hash',
      name: 'Hash Style',
      description: 'Social media hashtag style',
      icon: <Hash className="w-4 h-4" />,
      color: 'cyan',
      example: 'MyAwesomeBlogPost',
      separator: ''
    },
    {
      id: 'id',
      name: 'ID Format',
      description: 'Database ID friendly format',
      icon: <Code className="w-4 h-4" />,
      color: 'emerald',
      example: 'my-awesome-blog-post-123',
      separator: '-'
    },
    {
      id: 'custom',
      name: 'Custom Separator',
      description: 'Use your own separator',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet',
      example: 'my|awesome|blog|post',
      separator: '|'
    }
  ];

  const commonStopWords = [
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'he', 'in', 'is', 'it',
    'its', 'of', 'on', 'that', 'the', 'to', 'was', 'will', 'with', 'or', 'but', 'not', 'this',
    'these', 'those', 'they', 'them', 'their', 'we', 'us', 'our', 'you', 'your', 'i', 'my', 'me'
  ];

  const removeAccents = (str: string): string => {
    const accentMap: { [key: string]: string } = {
      'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a', 'å': 'a', 'æ': 'ae',
      'ç': 'c', 'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e', 'ì': 'i', 'í': 'i',
      'î': 'i', 'ï': 'i', 'ñ': 'n', 'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o',
      'ö': 'o', 'ø': 'o', 'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u', 'ý': 'y',
      'ÿ': 'y', 'ß': 'ss', 'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A',
      'Å': 'A', 'Æ': 'AE', 'Ç': 'C', 'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
      'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I', 'Ñ': 'N', 'Ò': 'O', 'Ó': 'O',
      'Ô': 'O', 'Õ': 'O', 'Ö': 'O', 'Ø': 'O', 'Ù': 'U', 'Ú': 'U', 'Û': 'U',
      'Ü': 'U', 'Ý': 'Y', 'Ÿ': 'Y'
    };

    return str.replace(/[àáâãäåæçèéêëìíîïñòóôõöøùúûüýÿßÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖØÙÚÛÜÝŸ]/g, (match) => {
      return accentMap[match] || match;
    });
  };

  const splitIntoWords = (text: string): string[] => {
    // Split by various delimiters and camelCase boundaries
    let words: string[] = [];
    
    // Handle camelCase/PascalCase input if enabled
    if (settings.camelCaseInput) {
      text = text.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    
    // Split by common delimiters
    const segments = text.split(/[\s\-_.,;:!?@#$%^&*()+=\[\]{}|<>'"~`/\\]+/);
    
    segments.forEach(segment => {
      if (!segment) return;
      
      // Further split camelCase boundaries
      const camelSplit = segment.split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/);
      words.push(...camelSplit.filter(word => word.length > 0));
    });

    return words;
  };

  const removeStopWordsFromText = (words: string[]): string[] => {
    if (!settings.removeStopWords) return words;
    
    let stopWords = [...commonStopWords];
    
    // Add custom stop words
    if (settings.customStopWords.trim()) {
      const customWords = settings.customStopWords
        .toLowerCase()
        .split(/[,\s]+/)
        .filter(word => word.length > 0);
      stopWords = [...stopWords, ...customWords];
    }
    
    return words.filter(word => !stopWords.includes(word.toLowerCase()));
  };

  const generateSlug = (text: string, method: string, options: SlugSettings): string => {
    if (!text.trim()) return '';
    
    let processedText = text;
    
    // Remove accents if enabled
    if (options.removeAccents) {
      processedText = removeAccents(processedText);
    }
    
    // Preserve file extensions if enabled
    let extension = '';
    if (options.preserveExtensions) {
      const match = processedText.match(/\.([\w]+)$/);
      if (match) {
        extension = match[0];
        processedText = processedText.replace(/\.([\w]+)$/, '');
      }
    }
    
    // Split into words
    let words = splitIntoWords(processedText);
    
    // Remove stop words
    words = removeStopWordsFromText(words);
    
    // Filter and clean words
    words = words
      .map(word => word.trim())
      .filter(word => word.length > 0)
      .map(word => {
        // Remove special characters if enabled
        if (options.removeSpecialChars) {
          word = word.replace(/[^\w\s\-]/g, '');
        }
        
        // Handle numbers
        if (options.removeNumbers) {
          word = word.replace(/\d/g, '');
        }
        
        return word;
      })
      .filter(word => word.length >= options.minLength);

    if (words.length === 0) return '';

    let result: string;
    const separator = method === 'custom' ? options.customSeparator : slugMethods.find(m => m.id === method)?.separator || '-';

    switch (method) {
      case 'kebab':
      case 'seo':
        result = words.map(word => word.toLowerCase()).join('-');
        break;

      case 'underscore':
      case 'filename':
        result = words.map(word => word.toLowerCase()).join('_');
        break;

      case 'dot':
        result = words.map(word => word.toLowerCase()).join('.');
        break;

      case 'camelCase':
        result = words.map((word, index) => {
          if (index === 0) return word.toLowerCase();
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join('');
        break;

      case 'PascalCase':
      case 'hash':
        result = words.map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('');
        break;

      case 'numbers':
        result = words.map(word => word.toLowerCase()).join('1');
        break;

      case 'compact':
        result = words.map(word => word.toLowerCase()).join('');
        break;

      case 'id':
        result = words.map(word => word.toLowerCase()).join('-');
        if (options.addRandomSuffix) {
          result += '-' + Math.random().toString(36).substr(2, 5);
        }
        break;

      case 'custom':
        if (options.preserveCase) {
          result = words.join(separator);
        } else {
          result = words.map(word => word.toLowerCase()).join(separator);
        }
        break;

      default:
        result = words.map(word => word.toLowerCase()).join('-');
    }

    // Apply case preservation if enabled
    if (options.preserveCase && !['camelCase', 'PascalCase', 'hash'].includes(method)) {
      // Don't change case for these methods
    }

    // Strict mode: extra cleaning
    if (options.strictMode) {
      result = result.replace(/[^a-zA-Z0-9\-_.]/g, '');
      result = result.replace(/[-_.]{2,}/g, separator || '-');
    }

    // Add prefix and suffix
    if (options.addPrefix) result = options.addPrefix + result;
    if (options.addSuffix) result = result + options.addSuffix;

    // Add timestamp if enabled
    if (options.addTimestamp) {
      const timestamp = Date.now().toString(36);
      result += (separator || '-') + timestamp;
    }

    // Add file extension back
    if (extension) {
      result += extension;
    }

    // Apply length limit
    if (result.length > options.maxLength) {
      result = result.substring(0, options.maxLength);
      // Clean up trailing separators
      result = result.replace(/[-_.]+$/, '');
    }

    return result;
  };

  const executeSlugGeneration = (text: string, method: string, options: SlugSettings): SlugResult => {
    if (!text.trim()) {
      return {
        inputItems: [],
        finalItems: [],
        method,
        output: '',
        stats: {
          originalCount: 0,
          finalCount: 0,
          emptyRemoved: 0,
          duplicatesRemoved: 0,
          totalInputLength: 0,
          totalOutputLength: 0,
          avgInputLength: 0,
          avgOutputLength: 0,
          longestInput: '',
          shortestInput: '',
          longestOutput: '',
          shortestOutput: '',
          compressionRatio: 0,
          urlSafeCount: 0,
          seoScore: 0
        }
      };
    }

    // Split input into items
    const inputItems = text.split('\n').map(item => item.replace(/\r$/, ''));
    let processedItems = [...inputItems];

    // Apply preprocessing
    if (options.trimWhitespace) {
      processedItems = processedItems.map(item => item.trim());
    }

    if (options.removeEmpty) {
      processedItems = processedItems.filter(item => item !== '');
    }

    // Remove duplicates
    if (options.removeDuplicates) {
      processedItems = Array.from(new Set(processedItems));
    }

    // Sort items
    if (options.sortItems) {
      processedItems = processedItems.sort((a, b) => {
        const comparison = a.toLowerCase().localeCompare(b.toLowerCase());
        return options.reverseSort ? -comparison : comparison;
      });
    }

    // Limit count
    if (options.limitCount && options.maxCount > 0) {
      processedItems = processedItems.slice(0, options.maxCount);
    }

    // Generate slugs
    const slugItems = processedItems.map(item => generateSlug(item, method, options));
    const output = slugItems.join('\n');

    // Calculate statistics
    const stats = computeSlugStatistics(inputItems, processedItems, slugItems);

    return {
      inputItems,
      finalItems: slugItems,
      method,
      output,
      stats
    };
  };

  const computeSlugStatistics = (inputItems: string[], processedItems: string[], outputItems: string[]): SlugStatistics => {
    const emptyRemoved = inputItems.filter(item => item.trim() === '').length;
    const duplicatesRemoved = inputItems.length - new Set(inputItems).size;
    
    const inputLengths = processedItems.map(item => item.length);
    const outputLengths = outputItems.map(item => item.length);
    
    const totalInputLength = inputLengths.reduce((sum, len) => sum + len, 0);
    const totalOutputLength = outputLengths.reduce((sum, len) => sum + len, 0);
    
    const avgInputLength = inputLengths.length > 0 ? Math.round(totalInputLength / inputLengths.length) : 0;
    const avgOutputLength = outputLengths.length > 0 ? Math.round(totalOutputLength / outputLengths.length) : 0;
    
    const longestInput = processedItems.reduce((longest, item) => item.length > longest.length ? item : longest, '');
    const shortestInput = processedItems.reduce((shortest, item) => item.length < shortest.length ? item : shortest, processedItems[0] || '');
    
    const longestOutput = outputItems.reduce((longest, item) => item.length > longest.length ? item : longest, '');
    const shortestOutput = outputItems.reduce((shortest, item) => item.length < shortest.length ? item : shortest, outputItems[0] || '');
    
    const compressionRatio = totalInputLength > 0 ? Math.round((totalOutputLength / totalInputLength) * 100) / 100 : 0;
    
    // URL safe count (only alphanumeric, hyphens, underscores, dots)
    const urlSafeCount = outputItems.filter(item => /^[a-zA-Z0-9\-_.]*$/.test(item)).length;
    
    // SEO score (based on length, readability, keyword-friendly)
    const seoScore = Math.round(outputItems.reduce((score, item) => {
      let itemScore = 100;
      if (item.length > 60) itemScore -= 20; // Too long
      if (item.length < 10) itemScore -= 10; // Too short
      if (!/^[a-zA-Z0-9\-]*$/.test(item)) itemScore -= 15; // Not URL-friendly
      if ((item.match(/\-/g) || []).length > 5) itemScore -= 10; // Too many separators
      return score + Math.max(0, itemScore);
    }, 0) / outputItems.length);

    return {
      originalCount: inputItems.length,
      finalCount: outputItems.length,
      emptyRemoved,
      duplicatesRemoved,
      totalInputLength,
      totalOutputLength,
      avgInputLength,
      avgOutputLength,
      longestInput,
      shortestInput,
      longestOutput,
      shortestOutput,
      compressionRatio,
      urlSafeCount,
      seoScore
    };
  };

  const slugResult: SlugResult = useMemo(() => {
    return executeSlugGeneration(inputText, selectedMethod, settings);
  }, [inputText, selectedMethod, settings]);

  useEffect(() => {
    setOutputText(slugResult.output);
  }, [slugResult]);

  const copyOutput = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopyMessage(true);
      setTimeout(() => setCopyMessage(false), 2000);
    }
  };

  const downloadOutput = () => {
    if (outputText) {
      const summary = `Slug Generator Results:
Method: ${slugMethods.find(m => m.id === selectedMethod)?.name}
Original Items: ${slugResult.stats.originalCount}
Final Items: ${slugResult.stats.finalCount}
Total Input Length: ${slugResult.stats.totalInputLength}
Total Output Length: ${slugResult.stats.totalOutputLength}
Compression Ratio: ${slugResult.stats.compressionRatio}
URL Safe Count: ${slugResult.stats.urlSafeCount}
SEO Score: ${slugResult.stats.seoScore}%

Output:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'slug-generator-output.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleTexts = () => {
    const sampleSets = [
      'How to Build Amazing Web Applications\nThe Ultimate Guide to JavaScript\nTop 10 React Best Practices\nModern CSS Techniques for Developers\nBuilding Responsive Websites in 2024',
      'My Awesome Blog Post Title\nBreaking News: Technology Updates\nProduct Launch: New Features Available\nBehind the Scenes: Company Culture\nCustomer Success: Case Study Analysis',
      'Travel Guide: Best Destinations 2024\nFood & Recipe: Healthy Cooking Tips\nFitness & Health: Morning Workout Routine\nLifestyle Blog: Minimalist Living\nDIY Projects: Home Improvement Ideas',
      'E-commerce Product: Wireless Headphones\nSoftware Tutorial: Getting Started Guide\nMobile App: User Interface Design\nWeb Development: Full Stack Course\nData Science: Machine Learning Basics',
      'SEO Optimization: Content Strategy\nDigital Marketing: Social Media Tips\nBrand Identity: Logo Design Process\nUX/UI Design: User Experience Research\nContent Creation: Video Production',
      'Academic Research: Climate Change Impact\nScientific Study: Renewable Energy Solutions\nEducational Content: Online Learning Platforms\nTechnology Review: Latest Smartphone Features\nInnovation Report: Artificial Intelligence Trends',
      'Real Estate: Property Investment Guide\nFinance Tips: Personal Budget Planning\nCareer Development: Interview Preparation\nEntrepreneurship: Startup Success Stories\nBusiness Strategy: Market Analysis Report',
      'Photography Tips: Portrait Lighting Techniques\nArt & Design: Creative Inspiration Sources\nMusic Production: Home Studio Setup\nWriting Guide: Novel Structure Planning\nCrafts & Hobbies: Beginner Pottery Lessons'
    ];
    setInputText(sampleSets[Math.floor(Math.random() * sampleSets.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Link className="text-green-600" />
            Slug Generator
          </h1>
          <p className="text-gray-600">Transform text into URL-friendly slugs with powerful customization options</p>
        </div>

        {/* Configuration Panel */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Slug Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleTexts}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Settings
              </button>
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Current Method
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="font-semibold text-gray-800">
                  {slugMethods.find(m => m.id === selectedMethod)?.name}
                </div>
                <div className="text-sm text-gray-600">
                  Example: {slugMethods.find(m => m.id === selectedMethod)?.example}
                </div>
              </div>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Slugs generated: <span className="font-semibold">{slugResult.stats.finalCount}</span></p>
                <p>URL safe: <span className="font-semibold">{slugResult.stats.urlSafeCount}</span></p>
                <p>SEO score: <span className="font-semibold">{slugResult.stats.seoScore}%</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Slug Settings</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Text Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeAccents}
                    onChange={(e) => setSettings({...settings, removeAccents: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Accents</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.trimWhitespace}
                    onChange={(e) => setSettings({...settings, trimWhitespace: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Trim Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeEmpty}
                    onChange={(e) => setSettings({...settings, removeEmpty: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeDuplicates}
                    onChange={(e) => setSettings({...settings, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.sortItems}
                    onChange={(e) => setSettings({...settings, sortItems: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Sort Items</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.reverseSort}
                    onChange={(e) => setSettings({...settings, reverseSort: e.target.checked})}
                    disabled={!settings.sortItems}
                    className="w-4 h-4 text-blue-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Reverse Sort</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.camelCaseInput}
                    onChange={(e) => setSettings({...settings, camelCaseInput: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Split camelCase Input</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Slug Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveCase}
                    onChange={(e) => setSettings({...settings, preserveCase: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Case</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.strictMode}
                    onChange={(e) => setSettings({...settings, strictMode: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Strict Mode (Extra Clean)</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeSpecialChars}
                    onChange={(e) => setSettings({...settings, removeSpecialChars: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Special Characters</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveNumbers}
                    onChange={(e) => setSettings({...settings, preserveNumbers: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeNumbers}
                    onChange={(e) => setSettings({...settings, removeNumbers: e.target.checked})}
                    disabled={settings.preserveNumbers}
                    className="w-4 h-4 text-blue-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Remove Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveExtensions}
                    onChange={(e) => setSettings({...settings, preserveExtensions: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve File Extensions</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.seoFriendly}
                    onChange={(e) => setSettings({...settings, seoFriendly: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">SEO Friendly</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.unicodeSupport}
                    onChange={(e) => setSettings({...settings, unicodeSupport: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Unicode Support</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Customization</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Custom Separator (for Custom method)</label>
                  <input
                    type="text"
                    value={settings.customSeparator}
                    onChange={(e) => setSettings({...settings, customSeparator: e.target.value})}
                    placeholder="Enter custom separator..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Add Prefix</label>
                  <input
                    type="text"
                    value={settings.addPrefix}
                    onChange={(e) => setSettings({...settings, addPrefix: e.target.value})}
                    placeholder="Prefix for each slug..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Add Suffix</label>
                  <input
                    type="text"
                    value={settings.addSuffix}
                    onChange={(e) => setSettings({...settings, addSuffix: e.target.value})}
                    placeholder="Suffix for each slug..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeStopWords}
                    onChange={(e) => setSettings({...settings, removeStopWords: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Stop Words</span>
                </label>

                {settings.removeStopWords && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Custom Stop Words</label>
                    <input
                      type="text"
                      value={settings.customStopWords}
                      onChange={(e) => setSettings({...settings, customStopWords: e.target.value})}
                      placeholder="Additional stop words (comma separated)..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.minLength}
                    onChange={(e) => setSettings({...settings, minLength: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.maxLength}
                    onChange={(e) => setSettings({...settings, maxLength: parseInt(e.target.value) || 100})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.addTimestamp}
                    onChange={(e) => setSettings({...settings, addTimestamp: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Add Timestamp</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.addRandomSuffix}
                    onChange={(e) => setSettings({...settings, addRandomSuffix: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Add Random Suffix (ID method)</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.limitCount}
                    onChange={(e) => setSettings({...settings, limitCount: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Limit Item Count</span>
                </label>

                {settings.limitCount && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max Items</label>
                    <input
                      type="number"
                      min="1"
                      value={settings.maxCount}
                      onChange={(e) => setSettings({...settings, maxCount: parseInt(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Input Text</label>
                <div className="text-sm text-gray-500">
                  {inputText.split('\n').filter(line => line.trim()).length} items
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to convert to slugs (one item per line)...&#10;Examples:&#10;How to Build Amazing Web Apps&#10;The Ultimate JavaScript Guide&#10;Top 10 React Best Practices&#10;Modern CSS Techniques&#10;Building Responsive Websites"
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Generated Slugs</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {outputText.length} characters
                  </div>
                  <button
                    onClick={copyOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copyMessage ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Generated slugs will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Slug Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Link className="text-green-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Slug Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {slugMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm mb-2 ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
                <div className={`text-xs font-mono p-2 rounded ${
                  selectedMethod === method.id ? 'bg-white bg-opacity-50' : 'bg-gray-100'
                }`}>
                  {method.example}
                </div>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {slugMethods.find(m => m.id === selectedMethod)?.name} Guide
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'kebab' && (
                <div>
                  <p>• Standard URL-friendly format with hyphens</p>
                  <p>• Perfect for URLs, permalinks, and web identifiers</p>
                  <p>• Example: "My Blog Post" → "my-blog-post"</p>
                </div>
              )}
              {selectedMethod === 'underscore' && (
                <div>
                  <p>• Uses underscores instead of hyphens</p>
                  <p>• Good for database fields and programming</p>
                  <p>• Example: "My Blog Post" → "my_blog_post"</p>
                </div>
              )}
              {selectedMethod === 'dot' && (
                <div>
                  <p>• Dot-separated format</p>
                  <p>• Common in package names and namespaces</p>
                  <p>• Example: "My Blog Post" → "my.blog.post"</p>
                </div>
              )}
              {selectedMethod === 'camelCase' && (
                <div>
                  <p>• camelCase format for APIs and programming</p>
                  <p>• First word lowercase, subsequent capitalized</p>
                  <p>• Example: "My Blog Post" → "myBlogPost"</p>
                </div>
              )}
              {selectedMethod === 'PascalCase' && (
                <div>
                  <p>• PascalCase format, all words capitalized</p>
                  <p>• Common for class names and components</p>
                  <p>• Example: "My Blog Post" → "MyBlogPost"</p>
                </div>
              )}
              {selectedMethod === 'numbers' && (
                <div>
                  <p>• Unique format using numbers as separators</p>
                  <p>• Creative alternative for special use cases</p>
                  <p>• Example: "My Blog Post" → "my1blog1post"</p>
                </div>
              )}
              {selectedMethod === 'compact' && (
                <div>
                  <p>• Removes all spaces, keeps letters and numbers</p>
                  <p>• Ultra-compact format for minimal URLs</p>
                  <p>• Example: "My Blog Post" → "myblogpost"</p>
                </div>
              )}
              {selectedMethod === 'seo' && (
                <div>
                  <p>• SEO-optimized with stop word removal</p>
                  <p>• Removes common words like "the", "and", "or"</p>
                  <p>• Example: "The Best Blog Post" → "best-blog-post"</p>
                </div>
              )}
              {selectedMethod === 'filename' && (
                <div>
                  <p>• Safe for file names across all operating systems</p>
                  <p>• Uses underscores and avoids problematic characters</p>
                  <p>• Example: "My Blog Post!" → "my_blog_post"</p>
                </div>
              )}
              {selectedMethod === 'hash' && (
                <div>
                  <p>• Social media hashtag style</p>
                  <p>• PascalCase without separators</p>
                  <p>• Example: "My Blog Post" → "MyBlogPost"</p>
                </div>
              )}
              {selectedMethod === 'id' && (
                <div>
                  <p>• Database ID friendly with optional random suffix</p>
                  <p>• Perfect for unique identifiers</p>
                  <p>• Example: "My Blog Post" → "my-blog-post-abc123"</p>
                </div>
              )}
              {selectedMethod === 'custom' && (
                <div>
                  <p>• Use your own custom separator</p>
                  <p>• Set the separator in settings above</p>
                  <p>• Example: "My Blog Post" → "my|blog|post"</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStatistics && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Slug Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Original Items</h3>
                <div className="text-2xl font-bold text-blue-900">{slugResult.stats.originalCount}</div>
                <p className="text-sm text-blue-600">Input items</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Generated Slugs</h3>
                <div className="text-2xl font-bold text-green-900">{slugResult.stats.finalCount}</div>
                <p className="text-sm text-green-600">Final slugs</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">URL Safe</h3>
                <div className="text-2xl font-bold text-orange-900">{slugResult.stats.urlSafeCount}</div>
                <p className="text-sm text-orange-600">Compatible slugs</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">SEO Score</h3>
                <div className="text-2xl font-bold text-purple-900">{slugResult.stats.seoScore}%</div>
                <p className="text-sm text-purple-600">Optimization score</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Input Length</h4>
                <div className="text-lg font-bold text-gray-900">{slugResult.stats.avgInputLength}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Slug Length</h4>
                <div className="text-lg font-bold text-gray-900">{slugResult.stats.avgOutputLength}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Compression Ratio</h4>
                <div className="text-lg font-bold text-gray-900">{slugResult.stats.compressionRatio}x</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Empty Removed</h4>
                <div className="text-lg font-bold text-gray-900">{slugResult.stats.emptyRemoved}</div>
              </div>
            </div>

            {/* Item Analysis */}
            {slugResult.stats.longestInput && slugResult.stats.shortestInput && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Item Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <h4 className="font-semibold text-emerald-800 mb-1">
                        Longest Input ({slugResult.stats.longestInput.length} chars)
                      </h4>
                      <div className="text-sm text-emerald-700 font-mono break-all">
                        {slugResult.stats.longestInput}
                      </div>
                    </div>
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <h4 className="font-semibold text-teal-800 mb-1">
                        Shortest Input ({slugResult.stats.shortestInput.length} chars)
                      </h4>
                      <div className="text-sm text-teal-700 font-mono break-all">
                        {slugResult.stats.shortestInput || '(empty)'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h4 className="font-semibold text-indigo-800 mb-1">
                        Longest Slug ({slugResult.stats.longestOutput.length} chars)
                      </h4>
                      <div className="text-sm text-indigo-700 font-mono break-all">
                        {slugResult.stats.longestOutput}
                      </div>
                    </div>
                    <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                      <h4 className="font-semibold text-cyan-800 mb-1">
                        Shortest Slug ({slugResult.stats.shortestOutput.length} chars)
                      </h4>
                      <div className="text-sm text-cyan-700 font-mono break-all">
                        {slugResult.stats.shortestOutput || '(empty)'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {slugResult.finalItems.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <List className="text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-800">Slug Preview</h2>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
              {slugResult.inputItems.slice(0, 20).map((item, index) => {
                const slugItem = slugResult.finalItems[index];
                const isUrlSafe = /^[a-zA-Z0-9\-_.]*$/.test(slugItem);
                return (
                  <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 font-mono">#{index + 1}</span>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500">
                          {item.length} → {slugItem?.length || 0} chars
                        </div>
                        {isUrlSafe ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">URL Safe</span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Check URL</span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Input:</div>
                        <div className="text-sm text-gray-700 bg-white p-2 rounded border break-all">
                          {item || '(empty)'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Slug:</div>
                        <div className="text-sm font-mono text-green-700 bg-green-50 p-2 rounded border border-green-200 break-all">
                          {slugItem || '(empty)'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {slugResult.inputItems.length > 20 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {slugResult.inputItems.length - 20} more items
                </div>
              )}
            </div>

            {/* Quick Slug Test */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-700 mb-2">Quick Test Examples:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                {[
                  'How to Build Amazing Web Applications',
                  'The Ultimate Guide to JavaScript',
                  'Top 10 React Best Practices',
                  'Modern CSS Techniques for Developers',
                  'Building Responsive Websites in 2024',
                  'API Documentation & Examples'
                ].map((example, index) => (
                  <div key={index} className="bg-white p-2 rounded border">
                    <div className="text-gray-600 text-xs mb-1 break-all">{example}</div>
                    <div className="text-green-700 font-mono font-semibold text-xs break-all">
                      {generateSlug(example, selectedMethod, settings)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* URL Examples */}
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-700 mb-2">Example URLs:</h3>
              <div className="space-y-2 text-sm">
                {slugResult.finalItems.slice(0, 3).map((slug, index) => (
                  <div key={index} className="bg-white p-2 rounded border font-mono text-xs break-all">
                    <span className="text-gray-500">https://example.com/blog/</span>
                    <span className="text-blue-700 font-semibold">{slug}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Stop Words Info */}
            {settings.removeStopWords && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-2">Stop Words Removed:</h3>
                <div className="text-sm text-yellow-700">
                  <p className="mb-2">Common words that are automatically removed when "Remove Stop Words" is enabled:</p>
                  <div className="font-mono text-xs bg-white p-2 rounded border">
                    {commonStopWords.slice(0, 20).join(', ')}...
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tips Section */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="text-yellow-600" />
            <h2 className="text-xl font-semibold text-gray-800">Slug Best Practices</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">SEO Friendly</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Keep slugs under 60 characters</li>
                <li>• Use descriptive keywords</li>
                <li>• Avoid stop words when possible</li>
                <li>• Use hyphens, not underscores</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">URL Safety</h3>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Only use letters, numbers, hyphens</li>
                <li>• Remove special characters</li>
                <li>• Convert to lowercase</li>
                <li>• Remove accents and diacritics</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-2">User Experience</h3>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Make slugs readable by humans</li>
                <li>• Keep them concise but descriptive</li>
                <li>• Use consistent formatting</li>
                <li>• Avoid numbers unless necessary</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-2">Quick Tips:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>For Blog Posts:</strong> Use kebab-case with SEO optimization</p>
                <p><strong>For Products:</strong> Include key features in the slug</p>
                <p><strong>For Categories:</strong> Keep short and descriptive</p>
              </div>
              <div>
                <p><strong>For APIs:</strong> Use camelCase or underscore format</p>
                <p><strong>For Files:</strong> Use filename-safe method</p>
                <p><strong>For Social:</strong> Use hashtag format without separators</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlugGenerator;